// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import L from 'leaflet';
import type { Bundle, RegionRow } from '../data';
import { stateColour, stateName } from '../data';
import { num, signed, signedPct, growthPct, pct1, tipAttr, escapeHtml } from '../format';
import { openDrawer } from '../components/overlay';

/**
 * Deliberately NOT a sequential choropleth — the fleet has dozens, and a fill
 * scaled by headcount just paints the whole map by where cities are. Instead the
 * polygons are thin neutral outlines and all data lives in centroid discs, with
 * a hollow ring frozen at a user-chosen ANCHOR year behind the filled disc for
 * the played year. The ring never moves, so scrubbing grows and shrinks each
 * disc against fixed geometry rather than making the whole map blink.
 */

interface Centroid { lat: number; lon: number }

/** Area-weighted centroid of a polygon's largest outer ring. */
function centroidOf(geom: GeoJSON.Geometry): Centroid | null {
  const rings: number[][][] = [];
  if (geom.type === 'Polygon') rings.push(...(geom.coordinates as number[][][]));
  else if (geom.type === 'MultiPolygon') for (const poly of geom.coordinates as number[][][][]) rings.push(poly[0]);
  else return null;
  let best: number[][] | null = null;
  let bestArea = -1;
  for (const ring of rings) {
    const a = Math.abs(ringArea(ring));
    if (a > bestArea) { bestArea = a; best = ring; }
  }
  if (!best || best.length < 3) return null;
  let x = 0, y = 0, a2 = 0;
  for (let i = 0, j = best.length - 1; i < best.length; j = i++) {
    const [x0, y0] = best[j];
    const [x1, y1] = best[i];
    const f = x0 * y1 - x1 * y0;
    a2 += f; x += (x0 + x1) * f; y += (y0 + y1) * f;
  }
  if (!a2) return { lon: best[0][0], lat: best[0][1] };
  return { lon: x / (3 * a2), lat: y / (3 * a2) };
}

function ringArea(ring: number[][]): number {
  let s = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1];
  }
  return s / 2;
}

export function renderMap(root: HTMLElement, b: Bundle, onPin: (name: string) => void): () => void {
  let resizeObserver: ResizeObserver | null = null;
  const years = b.map.years;
  const latest = b.map.latestYear;
  let anchor = b.map.baseYear;
  let played = latest;

  root.innerHTML = `
    <div class="view-head">
      <h2>Where the jobs are</h2>
      <p>Every Australian Public Service employee, placed in the <span class="glossary-link" data-term="sa4">SA4 region</span> they work in. Each disc is one region, sized by <span class="glossary-link" data-term="headcount">headcount</span>; the hollow ring behind it is that region frozen at the anchor year, so you can see growth against a fixed shape.</p>
    </div>
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Anchor</span>
        <select id="anchor-year"></select>
      </div>
      <div class="control-group" style="flex:1 1 320px;min-width:0">
        <span class="control-label">Year</span>
        <input type="range" id="year-scrub" min="0" max="${years.length - 1}" value="${years.length - 1}" aria-label="Year" />
        <output id="year-out" class="num" style="font-weight:600;min-width:4ch"></output>
      </div>
    </div>
    <div class="map-frame"><div id="map"></div></div>
    <p class="section-sub" style="margin-top:var(--space-md)" id="map-summary"></p>
    <div class="chip-row" id="offmap-chips"></div>
    <div class="legend">
      <span class="legend-item"><span class="legend-swatch" style="background:var(--accent-primary)"></span> Region in the played year</span>
      <span class="legend-item"><span class="legend-swatch" style="background:transparent;border:2px solid var(--text-tertiary)"></span> Same region at the anchor year</span>
      <span class="legend-item">Disc area is proportional to headcount. Colour shows state.</span>
    </div>`;

  const sel = root.querySelector<HTMLSelectElement>('#anchor-year')!;
  years.forEach((y) => {
    const o = document.createElement('option');
    o.value = String(y); o.textContent = String(y);
    if (y === anchor) o.selected = true;
    sel.appendChild(o);
  });

  const map = L.map(root.querySelector<HTMLElement>('#map')!, {
    center: [-27.5, 134], zoom: 4, scrollWheelZoom: true, worldCopyJump: false, minZoom: 3,
    // Fractional zoom. With the default zoomSnap of 1, fitBounds rounds DOWN to
    // a whole zoom level, so Australia is fitted by its height and then leaves
    // ~58% of a wide container as empty ocean — technically a correct fit, and
    // visually a bad map.
    zoomSnap: 0, zoomDelta: 0.5, wheelPxPerZoomLevel: 90,
  });
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> | Boundaries: ABS ASGS 2021 (CC BY 4.0)',
    maxZoom: 12,
  }).addTo(map);

  const byName = new Map<string, RegionRow>(b.map.regions.map((r) => [r.name, r]));
  const centroids = new Map<string, Centroid>();
  const boundaries = L.geoJSON(b.geo, {
    style: { color: '#9bacbd', weight: 0.7, fillColor: '#eef2f6', fillOpacity: 0.45 },
    onEachFeature: (f, layer) => {
      const name = (f.properties as Record<string, string>).sa4_name_2021;
      const c = centroidOf(f.geometry as GeoJSON.Geometry);
      if (c) centroids.set(name, c);
      layer.bindTooltip(() => tipFor(name), { className: 'aps-tip', sticky: true });
      layer.on('click', () => openRegion(name));
    },
  }).addTo(map);

  const discLayer = L.layerGroup().addTo(map);

  function valueAt(name: string, y: number): number | null {
    const r = byName.get(name);
    if (!r) return null;
    const v = r.values[String(y)];
    return v === undefined ? null : v;
  }

  function radius(v: number | null): number {
    if (v === null || v <= 0) return 0;
    // sqrt so AREA, not radius, encodes headcount.
    return Math.max(2.2, Math.sqrt(v) * 0.105);
  }

  function tipFor(name: string): string {
    const now = valueAt(name, played);
    const then = valueAt(name, anchor);
    const r = byName.get(name);
    const g = growthPct(then, now);
    const share = now !== null ? (now / b.meta.total) * 100 : null;
    if (now === null && then === null) {
      return `<strong>${escapeHtml(name)}</strong><br>No APS employees reported`;
    }
    return `<strong>${escapeHtml(name)}</strong><br>`
      + `${r?.state ? escapeHtml(stateName(r.state)) + '<br>' : ''}`
      + `${played}: <b>${num(now)}</b><br>`
      + `${anchor}: ${num(then)}<br>`
      + `Change: ${signed(now !== null && then !== null ? now - then : null)} (${signedPct(g)})<br>`
      + `${share !== null ? pct1(share) + ' of the APS' : ''}`;
  }

  function draw(): void {
    discLayer.clearLayers();
    for (const region of b.map.regions) {
      const c = centroids.get(region.name);
      if (!c) continue;
      const now = valueAt(region.name, played);
      const then = valueAt(region.name, anchor);
      const rAnchor = radius(then);
      const rNow = radius(now);
      const colour = stateColour(region.state);
      if (rAnchor > 0) {
        discLayer.addLayer(L.circleMarker([c.lat, c.lon], {
          radius: rAnchor, color: '#5b7c99', weight: 1.4, fill: false, interactive: false,
        }));
      }
      if (rNow > 0) {
        const m = L.circleMarker([c.lat, c.lon], {
          radius: rNow, color: colour, weight: 1, fillColor: colour, fillOpacity: 0.62,
        });
        m.bindTooltip(() => tipFor(region.name), { className: 'aps-tip', sticky: true });
        m.on('click', () => openRegion(region.name));
        discLayer.addLayer(m);
      }
    }
    const total = b.map.regions.reduce((s, r) => s + (valueAt(r.name, played) ?? 0), 0);
    const totalThen = b.map.regions.reduce((s, r) => s + (valueAt(r.name, anchor) ?? 0), 0);
    root.querySelector('#map-summary')!.textContent =
      `In ${played}, ${num(total)} APS employees worked in a mapped region — ${signed(total - totalThen)} `
      + `(${signedPct(growthPct(totalThen, total))}) against ${anchor}. Click any region for its full history.`;
    (root.querySelector('#year-out') as HTMLOutputElement).textContent = String(played);
  }

  function openRegion(name: string): void {
    const r = byName.get(name);
    if (!r) return;
    const series = years.map((y) => ({ y, v: valueAt(name, y) }));
    const vals = series.map((s) => s.v ?? 0);
    const max = Math.max(...vals, 1);
    const W = 460, H = 130, PAD = 26;
    const x = (i: number) => PAD + (i / (years.length - 1)) * (W - PAD - 8);
    const yy = (v: number) => H - 20 - (v / max) * (H - 34);
    const path = series.map((s, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${yy(s.v ?? 0).toFixed(1)}`).join('');
    const dots = series.map((s, i) => s.v === null ? '' :
      `<circle cx="${x(i).toFixed(1)}" cy="${yy(s.v).toFixed(1)}" r="2.4" fill="${stateColour(r.state)}"><title></title></circle>`).join('');
    const now = valueAt(name, latest);
    const then = valueAt(name, anchor);
    const rankNow = [...b.map.regions].sort((a, c) => (valueAt(c.name, latest) ?? 0) - (valueAt(a.name, latest) ?? 0))
      .findIndex((z) => z.name === name) + 1;

    openDrawer(`
      <h3>${escapeHtml(name)}</h3>
      <p class="drawer-sub">${escapeHtml(r.state ? stateName(r.state) : '')} · ranked ${rankNow} of ${b.map.regions.length} regions by APS headcount</p>
      <dl class="kv">
        <dt>Headcount, ${latest}</dt><dd>${num(now)}</dd>
        <dt>Headcount, ${anchor}</dt><dd>${num(then)}</dd>
        <dt>Change</dt><dd>${signed(now !== null && then !== null ? now - then : null)}</dd>
        <dt>Change, per cent</dt><dd>${signedPct(growthPct(then, now))}</dd>
        <dt>Share of the APS</dt><dd>${now !== null ? pct1((now / b.meta.total) * 100) : '—'}</dd>
      </dl>
      <h4 style="margin-top:var(--space-lg)">${years[0]} to ${latest}</h4>
      <div class="chart-wrap">
        <svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Headcount ${years[0]} to ${latest}">
          <line class="axis" x1="${PAD}" y1="${H - 20}" x2="${W - 8}" y2="${H - 20}" />
          <path d="${path}" fill="none" stroke="${stateColour(r.state)}" stroke-width="2" />
          ${dots}
          <text x="${PAD}" y="${H - 6}" font-size="10">${years[0]}</text>
          <text x="${W - 8}" y="${H - 6}" font-size="10" text-anchor="end">${latest}</text>
          <text x="${PAD}" y="12" font-size="10">peak ${num(max)}</text>
        </svg>
      </div>
      <p class="section-sub" style="margin-top:var(--space-md)">
        Counts are people, not full-time-equivalent positions, and place someone where their agency reports them.
      </p>
      <button type="button" class="icon-btn" id="pin-growth" style="background:var(--accent-primary);margin-top:var(--space-md)">Show this region in Growth</button>
    `);
    document.getElementById('pin-growth')?.addEventListener('click', () => onPin(name));
  }

  function renderChips(): void {
    const chips = b.map.offMap.map((o) => {
      const v = o.values[String(played)];
      return `<span class="chip" data-tip="${escapeHtml(tipAttr([`${o.name}`, `${played}: ${num(v ?? null)} employees`, 'No ABS boundary exists for this location, so it cannot be mapped.']))}">${escapeHtml(o.name)}: <span class="num">${num(v ?? null)}</span></span>`;
    }).join('');
    root.querySelector('#offmap-chips')!.innerHTML =
      `<span class="chip" style="background:none;border:none;padding-left:0">Not mappable:</span>${chips}`;
  }

  sel.addEventListener('change', () => { anchor = Number(sel.value); draw(); });
  const scrub = root.querySelector<HTMLInputElement>('#year-scrub')!;
  scrub.addEventListener('input', () => { played = years[Number(scrub.value)]; draw(); renderChips(); });

  draw();
  renderChips();
  // A Leaflet map created inside a container that has not been laid out yet
  // measures 0x0 and renders a grey void until it is told to re-measure. Fit to
  // the ACTUAL boundary extent rather than a hardcoded centre/zoom, so the map
  // frames Australia at whatever size the container turns out to be.
  const mapEl = root.querySelector<HTMLElement>('#map')!;
  let fitted = false;

  /**
   * Fit to the real boundary extent — but ONLY once the container has actually
   * been laid out.
   *
   * This is the trap: a Leaflet map created in a container that has not been
   * measured yet reports a size of 0x0, and `fitBounds` on a zero-size box
   * cannot fit anything, so it returns the layer's MAXIMUM zoom. The map then
   * renders perfectly — correct tiles, correct polygons, no error anywhere — of
   * a single street corner, with all 88 regions off-screen. Nothing in the
   * console and nothing in a unit test catches it; only looking at the page does.
   * Capping maxZoom hides the symptom while leaving the map fitted to nothing,
   * so the size guard below is the actual fix and the cap is only a backstop.
   */
  const fit = (): void => {
    map.invalidateSize();
    const { width, height } = mapEl.getBoundingClientRect();
    if (width < 50 || height < 50) return;
    const bounds = boundaries.getBounds();
    if (!bounds.isValid()) { map.setView([-27.5, 134], 4); fitted = true; return; }
    map.fitBounds(bounds, { padding: [10, 10], maxZoom: 7, animate: false });
    fitted = true;
  };

  // Once the reader zooms or drags, the view is theirs and must never be
  // yanked back. Until then the map's job is to frame Australia, so it re-fits
  // whenever the container changes size.
  let userMoved = false;
  map.on('zoomstart dragstart', () => { userMoved = true; });

  requestAnimationFrame(fit);
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => {
      // Opening any overlay sets `overflow: hidden` on the body, which removes
      // the scrollbar and widens this container. `invalidateSize()` alone
      // re-measures but drifts the centre, which left Australia stranded in a
      // corner next to Africa every time the About modal was opened.
      if (!fitted || !userMoved) fit();
      else map.invalidateSize();
    });
    ro.observe(mapEl);
    resizeObserver = ro;
  }

  return () => { resizeObserver?.disconnect(); map.remove(); };
}
